import { useCallback, useEffect, useRef, useState } from 'react';
import type { CaptchaProvider } from '@pryma/auth-shared';

// Minimal subsets of each provider's window-scoped global. We keep these
// hand-rolled rather than pulling in three sets of provider type packages —
// the surface we use is small and stable.
interface TurnstileGlobal {
  render: (
    container: HTMLElement | string,
    opts: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      size?: 'normal' | 'compact' | 'invisible';
      action?: string;
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
  execute: (widgetId: string) => void;
}

interface RecaptchaV3Global {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
}

interface HcaptchaGlobal {
  render: (
    container: HTMLElement,
    opts: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'light' | 'dark';
      size?: 'normal' | 'compact' | 'invisible';
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
  execute: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
    grecaptcha?: RecaptchaV3Global;
    hcaptcha?: HcaptchaGlobal;
  }
}

const SCRIPT_URLS: Record<CaptchaProvider, (siteKey: string) => string> = {
  turnstile: () => 'https://challenges.cloudflare.com/turnstile/v0/api.js',
  // reCAPTCHA v3 needs the site key in the script URL — that's how it
  // installs the always-on observer.
  recaptcha_v3: siteKey =>
    `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`,
  hcaptcha: () => 'https://js.hcaptcha.com/1/api.js',
};

const SCRIPT_ID: Record<CaptchaProvider, string> = {
  turnstile: 'authnz-turnstile-script',
  recaptcha_v3: 'authnz-recaptcha-v3-script',
  hcaptcha: 'authnz-hcaptcha-script',
};

// Cache of in-flight script loads so we don't insert the same <script> twice
// when several widgets mount in the same render.
const loadPromises = new Map<string, Promise<void>>();

function loadProviderScript(provider: CaptchaProvider, siteKey: string): Promise<void> {
  const id = SCRIPT_ID[provider];
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('document is not defined'));
  }

  const cached = loadPromises.get(id);
  if (cached) return cached;

  const existing = document.getElementById(id) as HTMLScriptElement | null;
  if (existing) {
    const ready = Promise.resolve();
    loadPromises.set(id, ready);
    return ready;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = id;
    script.src = SCRIPT_URLS[provider](siteKey);
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromises.delete(id);
      reject(new Error(`Failed to load ${provider} script`));
    };
    document.head.appendChild(script);
  });

  loadPromises.set(id, promise);
  return promise;
}

export interface UseCaptchaOptions {
  provider: CaptchaProvider;
  siteKey: string;
  // Required for reCAPTCHA v3, optional for others.
  action?: string;
  onError?: (err: Error) => void;
}

export interface UseCaptchaResult {
  isReady: boolean;
  // Imperatively run an invisible challenge and resolve to a fresh token.
  // For interactive widgets this is a no-op; the token comes via onVerify.
  execute: () => Promise<string | null>;
}

// Hook for invisible/imperative CAPTCHA execution. For interactive widget
// rendering use <CaptchaWidget /> instead.
export function useCaptcha({
  provider,
  siteKey,
  action = 'submit',
  onError,
}: UseCaptchaOptions): UseCaptchaResult {
  const [isReady, setIsReady] = useState(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    if (!siteKey) {
      setIsReady(false);
      return;
    }
    loadProviderScript(provider, siteKey)
      .then(() => {
        if (cancelled) return;
        // reCAPTCHA needs an explicit ready() callback before execute() works.
        if (provider === 'recaptcha_v3' && window.grecaptcha) {
          window.grecaptcha.ready(() => {
            if (!cancelled) setIsReady(true);
          });
        } else {
          setIsReady(true);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) onErrorRef.current?.(err);
      });
    return () => {
      cancelled = true;
    };
  }, [provider, siteKey]);

  const execute = useCallback(async (): Promise<string | null> => {
    if (!isReady) return null;
    try {
      if (provider === 'recaptcha_v3' && window.grecaptcha) {
        return await window.grecaptcha.execute(siteKey, { action });
      }
      // Turnstile/hCaptcha invisible execution requires a rendered widget,
      // which is the CaptchaWidget component's job. Hook callers using those
      // providers should prefer the component.
      return null;
    } catch (err) {
      onErrorRef.current?.(err instanceof Error ? err : new Error(String(err)));
      return null;
    }
  }, [provider, siteKey, action, isReady]);

  return { isReady, execute };
}

// Re-export the script loader so the widget component can share the cache.
export { loadProviderScript };
